import { html, nothing } from "lit-html";
import type { View, App } from "../app.js";
import { api, ApiError } from "../api.js";
import { icons } from "../icons.js";
import type { AdminUser, Employee } from "../types.js";
import { sectionHead } from "../components.js";
import { fmtDate } from "../format.js";
import qrcode from "qrcode-generator";
import { unsafeSVG } from "lit-html/directives/unsafe-svg.js";

function qrSvg(text: string) {
  const q = qrcode(0, "M");
  q.addData(text);
  q.make();
  return unsafeSVG(q.createSvgTag({ cellSize: 5, margin: 3, scalable: true }));
}

let tfaSetup: { secret: string; uri: string } | null = null;
let tfaCodes: string[] | null = null;
let tfaMsg: { ok: boolean; text: string } | null = null;
let tfaOff = false;

function twoFactorCard(app: App) {
  const t = app.me.twoFactor;
  const start = async () => {
    tfaMsg = null;
    try { tfaSetup = await api.twofaSetup(); } catch (e) { tfaMsg = { ok: false, text: errText(e) }; }
    app.render();
  };
  const confirm = async (e: Event) => {
    e.preventDefault();
    try {
      const r = await api.twofaEnable(val(e.target as HTMLFormElement, "code").trim());
      tfaCodes = r.recoveryCodes; tfaSetup = null;
      app.me.twoFactor = { ...t, enabled: true, recoveryLeft: r.recoveryCodes.length };
      tfaMsg = { ok: true, text: "Two-factor is on. From now on you'll enter a code from your phone when you sign in." };
    } catch (err) { tfaMsg = { ok: false, text: errText(err) }; }
    app.render();
  };
  const disable = async (e: Event) => {
    e.preventDefault();
    try {
      await api.twofaDisable(val(e.target as HTMLFormElement, "password"));
      app.me.twoFactor = { ...t, enabled: false, recoveryLeft: 0 };
      tfaOff = false; tfaCodes = null;
      tfaMsg = { ok: true, text: t.required ? "Two-factor is off. It's required here, so set it up again on your new phone now." : "Two-factor is off." };
    } catch (err) { tfaMsg = { ok: false, text: errText(err) }; }
    app.render();
  };
  return html`<section class="glass card mt">
    ${sectionHead("Two-factor sign-in", t.enabled ? `On · ${t.recoveryLeft} recovery code${t.recoveryLeft === 1 ? "" : "s"} left` : t.required ? "Required for everyone. Set it up to keep using the dashboard." : "Off. Adds a 6-digit code from your phone when you sign in.")}
    ${tfaMsg ? html`<div class=${tfaMsg.ok ? "ok-text" : "err-text"} style="margin-bottom:12px">${tfaMsg.text}</div>` : nothing}
    ${tfaCodes ? html`<div class="banner" style="margin-bottom:14px;display:block">
        <b>Save these recovery codes now.</b> Each one gets you in once if you lose your phone. They won't be shown again.
        <div class="recovery">${tfaCodes.map((c) => html`<code>${c}</code>`)}</div>
        <button class="link" @click=${() => { tfaCodes = null; app.render(); }}>I saved them</button>
      </div>` : nothing}
    ${!t.enabled && !tfaSetup ? html`<button class="btn primary" @click=${start}>${icons.key}<span>Set up two-factor</span></button>` : nothing}
    ${tfaSetup ? html`<div class="grid g-2e" style="align-items:start">
        <ol class="steps">
          <li>Install an authenticator app if you don't have one: Google Authenticator, Microsoft Authenticator, or the iPhone Passwords app all work.</li>
          <li>In the app, add an account and <b>scan this QR code</b>. On this same phone? Tap the setup key below to copy it and enter it by hand.</li>
          <li>Type the 6-digit code the app shows to finish.</li>
        </ol>
        <div class="stack" style="gap:12px">
          <div class="qr">${qrSvg(tfaSetup.uri)}</div>
          <div class="field"><label>Setup key</label><div class="code" style="word-break:break-all;user-select:all">${tfaSetup.secret.replace(/(.{4})/g, "$1 ").trim()}</div></div>
          <form class="row" style="gap:10px" @submit=${confirm}>
            <input class="input" name="code" inputmode="numeric" autocomplete="one-time-code" placeholder="6-digit code" maxlength="7" required style="width:160px">
            <button class="btn primary" type="submit">Turn on</button>
            <button class="btn" type="button" @click=${() => { tfaSetup = null; app.render(); }}>Cancel</button>
          </form>
        </div>
      </div>` : nothing}
    ${t.enabled ? html`<div class="mt">${tfaOff
      ? html`<form class="row" style="gap:10px;flex-wrap:wrap" @submit=${disable}>
          <input class="input" type="password" name="password" placeholder="Your password" autocomplete="current-password" required style="width:240px">
          <button class="btn danger" type="submit">Turn off two-factor</button><button class="btn" type="button" @click=${() => { tfaOff = false; app.render(); }}>Cancel</button>
        </form>`
      : html`<button class="link" @click=${() => { tfaOff = true; app.render(); }}>New phone? Turn two-factor off, then set it up again</button>`}</div>` : nothing}
  </section>`;
}

/** A strong random password the admin can copy and send (passwords are stored scrambled, so they can't be looked up later). */
function generatePassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const a = new Uint32Array(14); crypto.getRandomValues(a);
  let p = Array.from(a, (n) => chars[n % chars.length]).join("");
  if (!/\d/.test(p)) p = p.slice(0, 13) + "7";
  if (!/[a-z]/i.test(p)) p = "k" + p.slice(1);
  return p.slice(0, 7) + "-" + p.slice(7);
}
const fillGenerated = (e: Event) => {
  const input = (e.target as HTMLElement).closest("form")?.querySelector<HTMLInputElement>("input[name=password]");
  if (input) { input.type = "text"; input.value = generatePassword(); input.select(); }
};

const errText = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const val = (form: HTMLFormElement, name: string) => String(new FormData(form).get(name) ?? "");

/* ---------------- Account (everyone) ---------------- */

let pwMsg: { ok: boolean; text: string } | null = null;
let pwBusy = false;

export const account: View<{ ok: true }> = {
  title: () => "Account",
  filters: [],
  async load() { pwMsg = null; tfaMsg = null; return { ok: true }; },
  render(app: App) {
    const submit = async (e: Event) => {
      e.preventDefault();
      const f = e.target as HTMLFormElement;
      const next = val(f, "next");
      if (next !== val(f, "confirm")) { pwMsg = { ok: false, text: "The two new passwords don't match." }; app.render(); return; }
      pwBusy = true; app.render();
      try {
        await api.changePassword(val(f, "current"), next);
        pwMsg = { ok: true, text: "Password changed. You're signed out on every other device." };
        f.reset();
      } catch (err) { pwMsg = { ok: false, text: errText(err) }; }
      pwBusy = false; app.render();
    };
    const u = app.me.user;
    return html`
      <div class="grid g-2e">
        <section class="glass card">
          ${sectionHead("You", u.role === "owner" ? "Owner: sees all of Feld Fire" : `Manager: sees ${app.me.scope.label}`)}
          <div class="kv-list">
            <div class="kv"><span class="muted">Name</span><b>${u.name}</b></div>
            <div class="kv"><span class="muted">Username</span><b>${u.username}</b></div>
            <div class="kv"><span class="muted">Access</span><b>${u.role === "owner" ? "Owner" : "Manager"}${u.isAdmin ? " · Admin" : ""}</b></div>
          </div>
          <div class="row mt" style="gap:10px">
            <button class="btn" @click=${() => { const next = document.documentElement.dataset.theme === "light" ? "dark" : "light"; document.documentElement.dataset.theme = next; try { localStorage.setItem("ffd-theme", next); } catch { /* private mode */ } app.reload(); }}>Switch light / dark</button>
            <button class="btn" @click=${async () => { await api.logout().catch(() => undefined); location.reload(); }}>Sign out</button>
          </div>
        </section>
        <section class="glass card">
          ${sectionHead("Change password", "At least 10 characters, with a letter and a number")}
          <form class="stack" style="gap:12px" @submit=${submit}>
            <label class="field"><span>Current password</span><input class="input" type="password" name="current" autocomplete="current-password" required></label>
            <label class="field"><span>New password</span><input class="input" type="password" name="next" autocomplete="new-password" minlength="10" required></label>
            <label class="field"><span>New password again</span><input class="input" type="password" name="confirm" autocomplete="new-password" minlength="10" required></label>
            ${pwMsg ? html`<div class=${pwMsg.ok ? "ok-text" : "err-text"}>${pwMsg.text}</div>` : nothing}
            <div><button class="btn primary" type="submit" ?disabled=${pwBusy}>${pwBusy ? "Saving…" : "Change password"}</button></div>
          </form>
        </section>
      </div>
      ${twoFactorCard(app)}`;
  },
};

/* ---------------- Users (admins) ---------------- */

interface UsersData { users: AdminUser[]; employees: Employee[]; require2fa: boolean }
let addMsg: { ok: boolean; text: string } | null = null;
let rowMsg: { id: number; ok: boolean; text: string } | null = null;
let resetFor: number | null = null;
let editFor: number | null = null;
let editRole: "owner" | "manager" = "manager";
let newRole: "owner" | "manager" = "manager";

export const users: View<UsersData> = {
  title: () => "Users",
  filters: [],
  async load(app) {
    if (!app.me.user.isAdmin) throw new Error("Only an admin can manage accounts.");
    return api.users();
  },
  render(app: App, d) {
    const refresh = async () => { const fresh = await api.users(); d.users = fresh.users; d.employees = fresh.employees; d.require2fa = fresh.require2fa; app.render(); };
    const toggleRequire = async (e: Event) => {
      const on = (e.target as HTMLInputElement).checked;
      try { await api.setRequire2fa(on); app.me.twoFactor = { ...app.me.twoFactor, required: on }; await refresh(); app.toast(on ? "Two-factor is now required for everyone" : "Two-factor is optional again"); }
      catch (err) { (e.target as HTMLInputElement).checked = !on; app.toast(errText(err)); }
    };
    const add = async (e: Event) => {
      e.preventDefault();
      const f = e.target as HTMLFormElement;
      const emp = val(f, "employee");
      try {
        await api.createUser({ username: val(f, "username").trim(), name: val(f, "name").trim(), role: newRole, employeeId: emp ? Number(emp) : null, isAdmin: new FormData(f).get("admin") === "on", password: val(f, "password") });
        addMsg = { ok: true, text: `Created ${val(f, "username").trim()} with password ${val(f, "password")}. Copy it now and send it to them: passwords are stored scrambled, so nobody (including you) can look it up later. They can change it under Account.` };
        f.reset(); newRole = "manager";
        await refresh();
      } catch (err) { addMsg = { ok: false, text: errText(err) }; app.render(); }
    };
    const update = async (u: AdminUser, change: Parameters<typeof api.updateUser>[1], done: string) => {
      try { await api.updateUser(u.id, change); rowMsg = { id: u.id, ok: true, text: done }; await refresh(); }
      catch (err) { rowMsg = { id: u.id, ok: false, text: errText(err) }; app.render(); }
    };
    const reset = async (e: Event, u: AdminUser) => {
      e.preventDefault();
      try {
        const pw = val(e.target as HTMLFormElement, "password");
        await api.resetPassword(u.id, pw);
        rowMsg = { id: u.id, ok: true, text: `Password for ${u.username} is now ${pw}. Copy it now; it can't be shown again. They're signed out everywhere and their Claude connector link was turned off.` };
        resetFor = null; app.render();
      } catch (err) { rowMsg = { id: u.id, ok: false, text: errText(err) }; app.render(); }
    };
    const saveEdit = async (e: Event, u: AdminUser) => {
      e.preventDefault();
      const f = e.target as HTMLFormElement;
      const emp = val(f, "employee");
      const change = { username: val(f, "username").trim(), name: val(f, "name").trim(), role: editRole, employeeId: emp ? Number(emp) : null, isAdmin: new FormData(f).get("admin") === "on" };
      try { await api.updateUser(u.id, change); editFor = null; rowMsg = { id: u.id, ok: true, text: `Saved. ${change.username !== u.username ? `They now sign in as ${change.username}.` : ""}` }; await refresh(); }
      catch (err) { rowMsg = { id: u.id, ok: false, text: errText(err) }; app.render(); }
    };
    const empOptions = (sel: number | null) => html`<option value="" ?selected=${sel === null}>None</option>${d.employees.map((x) => html`<option value=${x.id} ?selected=${sel === x.id}>${x.name}${x.title ? ` (${x.title})` : ""}</option>`)}`;
    return html`
      <section class="glass card">
        <div class="row" style="align-items:flex-start;flex-wrap:wrap;gap:12px">
          ${sectionHead("Logins", `${d.users.filter((u) => !u.disabled).length} active · ${d.users.filter((u) => !u.disabled && u.twoFactor).length} with two-factor`)}
          <span class="spacer"></span>
          <label class="check" title="Everyone must set up two-factor before they can see any data"><input type="checkbox" .checked=${d.require2fa} @change=${toggleRequire}><span>Require two-factor for everyone</span></label>
        </div>
        <div class="table-wrap"><table class="t">
          <thead><tr><th>Name</th><th>Username</th><th>Access</th><th>Sees</th><th>2FA</th><th class="r"></th></tr></thead>
          <tbody>${d.users.map((u) => html`
            <tr class=${u.disabled ? "muted" : ""}>
              <td class="name">${u.name}${u.isAdmin ? html` <span class="tag">Admin</span>` : nothing}${u.disabled ? html` <span class="tag">Disabled</span>` : nothing}</td>
              <td>${u.username}</td>
              <td><select class="select" style="min-width:110px" @change=${(e: Event) => update(u, { role: (e.target as HTMLSelectElement).value as "owner" | "manager" }, "Access updated.")}>
                <option value="owner" ?selected=${u.role === "owner"}>Owner</option><option value="manager" ?selected=${u.role === "manager"}>Manager</option></select></td>
              <td>${u.role === "owner" ? html`<span class="muted">Everything${u.employeeId !== null ? ` · ${d.employees.find((x) => x.id === u.employeeId)?.name ?? ""}` : ""}</span>` : html`<select class="select" @change=${(e: Event) => { const v = (e.target as HTMLSelectElement).value; void update(u, { employeeId: v ? Number(v) : null }, "Team updated."); }}>${empOptions(u.employeeId)}</select>`}</td>
              <td>${u.twoFactor ? html`<span class="ok-text">On</span>` : html`<span class="muted">Off</span>`}</td>
              <td class="r" style="white-space:nowrap">
                <button class="link" @click=${() => { editFor = editFor === u.id ? null : u.id; editRole = u.role; resetFor = null; rowMsg = null; app.render(); }}>Edit</button>
                · <button class="link" @click=${() => { resetFor = resetFor === u.id ? null : u.id; editFor = null; rowMsg = null; app.render(); }}>Reset password</button>
                ${u.twoFactor ? html` · <button class="link" title="For a lost phone: turns their two-factor off so they can sign in and set it up again" @click=${async () => { try { await api.reset2fa(u.id); rowMsg = { id: u.id, ok: true, text: "Two-factor reset. They can sign in with their password and set it up again." }; await refresh(); } catch (err) { rowMsg = { id: u.id, ok: false, text: errText(err) }; app.render(); } }}>Reset 2FA</button>` : nothing}
                · <button class="link" @click=${() => update(u, { disabled: !u.disabled }, u.disabled ? "Enabled." : "Disabled and signed out.")}>${u.disabled ? "Enable" : "Disable"}</button>
              </td>
            </tr>
            ${editFor === u.id ? html`<tr><td colspan="6">
              <form class="grid g-2e" style="gap:12px;padding:6px 0" @submit=${(e: Event) => saveEdit(e, u)}>
                <label class="field"><span>Full name</span><input class="input" name="name" required .value=${u.name}></label>
                <label class="field"><span>Username (what they sign in with)</span><input class="input" name="username" required pattern="[A-Za-z0-9._-]{2,40}" autocomplete="off" .value=${u.username}></label>
                <label class="field"><span>Access</span><select class="select" name="role" @change=${(e: Event) => { editRole = (e.target as HTMLSelectElement).value as "owner" | "manager"; app.render(); }}>
                  <option value="owner" ?selected=${editRole === "owner"}>Owner: everything, can switch teams</option><option value="manager" ?selected=${editRole === "manager"}>Manager: their team only</option></select></label>
                <label class="field"><span>${editRole === "manager" ? "Who they are in NetSuite (sets their team)" : "Who they are in NetSuite (opens on their team, if they run one)"}</span>
                  <select class="select" name="employee" ?required=${editRole === "manager"}>${empOptions(u.employeeId)}</select></label>
                <label class="check"><input type="checkbox" name="admin" .checked=${u.isAdmin}><span>Admin: can manage logins</span></label>
                <div class="row" style="gap:8px;grid-column:1/-1"><button class="btn primary" type="submit">Save</button><button class="btn" type="button" @click=${() => { editFor = null; app.render(); }}>Cancel</button>
                  <span class="muted small">Passwords: use Reset password. Nobody can see an existing one.</span></div>
              </form></td></tr>` : nothing}
            ${resetFor === u.id ? html`<tr><td colspan="6">
              <form class="row" style="gap:10px;flex-wrap:wrap" @submit=${(e: Event) => reset(e, u)}>
                <input class="input" style="width:260px" type="password" name="password" placeholder="New password for ${u.username}" autocomplete="new-password" minlength="10" required>
                <button class="btn" type="button" @click=${fillGenerated}>Generate</button>
                <button class="btn primary" type="submit">Set password</button><button class="btn" type="button" @click=${() => { resetFor = null; app.render(); }}>Cancel</button>
              </form></td></tr>` : nothing}
            ${rowMsg?.id === u.id ? html`<tr><td colspan="6"><span class=${rowMsg.ok ? "ok-text" : "err-text"}>${rowMsg.text}</span></td></tr>` : nothing}`)}
          </tbody>
        </table></div>
      </section>
      <section class="glass card mt">
        ${sectionHead("Add a login", "Owners see everything. Managers see only their NetSuite team, and never Security & Extinguisher.")}
        <form class="grid g-2e" style="gap:14px" @submit=${add}>
          <label class="field"><span>Full name</span><input class="input" name="name" required placeholder="Matt Pottebaum"></label>
          <label class="field"><span>Username</span><input class="input" name="username" required pattern="[A-Za-z0-9._-]{2,40}" placeholder="matt" autocomplete="off"></label>
          <label class="field"><span>Access</span><select class="select" name="role" @change=${(e: Event) => { newRole = (e.target as HTMLSelectElement).value as "owner" | "manager"; app.render(); }}>
            <option value="manager" ?selected=${newRole === "manager"}>Manager: their team only</option><option value="owner" ?selected=${newRole === "owner"}>Owner: everything</option></select></label>
          <label class="field"><span>${newRole === "manager" ? "Their NetSuite employee (sets whose team they see)" : "Their NetSuite employee (optional)"}</span>
            <select class="select" name="employee" ?required=${newRole === "manager"}>${empOptions(null)}</select></label>
          <label class="field"><span>Starting password (10+ characters, a letter and a number)</span>
            <div class="row" style="gap:8px"><input class="input" type="password" name="password" minlength="10" required autocomplete="new-password" style="flex:1"><button class="btn" type="button" @click=${fillGenerated}>Generate</button></div></label>
          <label class="check" style="align-self:end"><input type="checkbox" name="admin"><span>Admin: can manage logins</span></label>
          <div style="grid-column:1/-1">
            ${addMsg ? html`<div class=${addMsg.ok ? "ok-text" : "err-text"} style="margin-bottom:10px">${addMsg.text}</div>` : nothing}
            <button class="btn primary" type="submit">Create login</button>
          </div>
        </form>
      </section>`;
  },
};
